import React, { useState, useEffect } from 'react';
import { Card, message, Button, Space, Row, Col, Input, Select, Typography, Table } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { useProduccionContext } from '../../context/ProduccionContext';
import { theme } from '../../theme';


const { Title, Text } = Typography;
const { Option } = Select;

// Nuevo formato de respuesta del backend
interface RegistroProduccion {
  id: number;                    // ID de balanza
  numero_ticket: number;         // Número de ticket
  rollo_id: number | null;       // ID del rollo (null si no hay rollos)
  rollo_identificacion: string;  // Identificación del rollo
  fecha: string;                 // Fecha del ticket
  compra_servicio: string;       // Tipo de compra/servicio
  cliente: {
    id: number;
    razon: string;
  };
  productor: {
    id: number;
    razon: string;
  };
  producto: {
    id: number;
    descripcion: string;
    nivel?: string;
  };
  peso_neto: number;             // Kilos netos por rollo
  desmotado: boolean;            // True/False si está desmotado
  presentacion: number | null;   // FK a Presentacion (granel/módulo/rollo)
}

// Mantener la interfaz Pesaje para compatibilidad temporal
interface Pesaje {
  id: number;
  numero_ticket: number;
  fecha_ticket: string;
  cliente: {
    id: number;
    razon: string;
  };
  productor: {
    id: number;
    razon: string;
  };
  producto: {
    id: number;
    descripcion: string;
     nivel?: string;
  };
  establecimiento: {
    id: number;
    descripcion: string;
  };
  nlote: {
    id: number;
    descripcion: string;
  };
  kilos_primera: number;
  kilos_segunda: number;
  peso_neto: number;
  rollos: any[];
  cantidad_rollos: number;
}

interface Producto {
  id: number;
  descripcion: string;
  nivel: string;
}

interface TicketValidado {
  numero_ticket: string;
  // ... otros campos que vengan del backend
}

const A_Produccion: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const { getAccessTokenSilently, user } = useAuth0();
  const [ticket1, setTicket1] = useState('');
  const [ticket2, setTicket2] = useState('');
  const [ticket3, setTicket3] = useState('');
  const [ticket4, setTicket4] = useState('');
  const [productos, setProductos] = useState<Producto[]>([]);
  const [productoSeleccionado, setProductoSeleccionado] = useState<string>();
  const [productoCompleto, setProductoCompleto] = useState<Producto | null>(null);
  const [registros, setRegistros] = useState<RegistroProduccion[]>([]);
  const [pesajes, setPesajes] = useState<Pesaje[]>([]);
  const [hayResultados, setHayResultados] = useState(false);
  const [ticketsValidados, setTicketsValidados] = useState<string[]>(['0', '0', '0', '0']);
  const [hayTicketsValidados, setHayTicketsValidados] = useState(false);
  const [rollosSeleccionados, setRollosSeleccionados] = useState<{[key: string]: boolean}>({});
  const [todosRollosSeleccionados, setTodosRollosSeleccionados] = useState(false);
  const navigate = useNavigate();
  const { setProduccionData } = useProduccionContext();

  // Obtener id_empresa e id_sucursal del usuario autenticado
  const userMetadata = user as unknown as { id_empresa: number; id_sucursal: number };
  const id_empresa = userMetadata?.id_empresa;
  const id_sucursal = userMetadata?.id_sucursal;

  // Función para agrupar registros por ticket (compatibilidad con formato anterior)
  const agruparRegistrosPorTicket = (registros: RegistroProduccion[]): Pesaje[] => {
    const ticketsAgrupados: { [key: number]: Pesaje } = {};
    
    registros.forEach(registro => {
      if (!ticketsAgrupados[registro.numero_ticket]) {
        ticketsAgrupados[registro.numero_ticket] = {
          id: registro.id,
          numero_ticket: registro.numero_ticket,
          fecha_ticket: registro.fecha,
          cliente: registro.cliente,
          productor: registro.productor,
          producto: registro.producto,
          establecimiento: { id: 0, descripcion: '' },
          nlote: { id: 0, descripcion: '' },
          kilos_primera: 0,
          kilos_segunda: 0,
          peso_neto: 0,
          rollos: [],
          cantidad_rollos: 0
        };
      }
      
      // Agregar rollo al ticket
      if (registro.rollo_id) {
        ticketsAgrupados[registro.numero_ticket].rollos.push({
          id: registro.rollo_id,
          rollo_identificacion: registro.rollo_identificacion,
          tipo: '',
          alta: 1,
          desmotado: registro.desmotado
        });
      }
      
      // Sumar peso neto
      ticketsAgrupados[registro.numero_ticket].peso_neto += registro.peso_neto;
    });
    
    // Convertir a array y actualizar cantidad de rollos
    return Object.values(ticketsAgrupados).map(ticket => ({
      ...ticket,
      cantidad_rollos: ticket.rollos.length
    }));
  };

  // Columnas para la tabla de registros (nuevo formato)
  const registrosColumns: ColumnsType<RegistroProduccion> = [
    {
      title: 'Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 80,
    },
    {
      title: 'Rollo',
      dataIndex: 'rollo_identificacion',
      key: 'rollo_identificacion',
      width: 150,
      render: (text, record) => (
        <span style={{
          color: record.desmotado ? '#999' : '#000',
          fontStyle: record.desmotado ? 'italic' : 'normal',
          textDecoration: record.desmotado ? 'line-through' : 'none'
        }}>
          {text || 'Sin rollo'}
          {record.desmotado && ' (Desmotado)'}
        </span>
      ),
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha',
      key: 'fecha',
      width: 120,
    },
    {
      title: 'Cliente',
      dataIndex: ['cliente', 'razon'],
      key: 'cliente',
      width: 200,
    },
    {
      title: 'Productor',
      dataIndex: ['productor', 'razon'],
      key: 'productor',
      width: 200,
    },
    {
      title: 'Producto',
      dataIndex: ['producto', 'descripcion'],
      key: 'producto',
      width: 150,
    },
    {
      title: 'Peso Neto',
      dataIndex: 'peso_neto',
      key: 'peso_neto',
      width: 100,
      align: 'right',
      render: (value) => `${value.toLocaleString()} kg`,
    },
    {
      title: 'Seleccionar',
      key: 'seleccionar',
      width: 100,
      render: (_, record) => {
        const key = `${record.numero_ticket}-${record.rollo_id || 'no-rollo'}`;
        const isSelected = rollosSeleccionados[key] || false;
        
        return (
          <input
            type="checkbox"
            checked={isSelected}
            disabled={record.desmotado}
            onChange={(e) => handleSeleccionarRegistro(record, e.target.checked)}
            style={{
              opacity: record.desmotado ? 0.5 : 1,
              cursor: record.desmotado ? 'not-allowed' : 'pointer',
              backgroundColor: record.desmotado ? '#f5f5f5' : 'white'
            }}
            title={record.desmotado ? 'Registro ya desmotado' : 'Seleccionar registro'}
          />
        );
      },
    },
  ];

  // Mantener columnas originales para compatibilidad
  const columns: ColumnsType<Pesaje> = [
    {
      title: 'Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 80,
    },
    {
      title: 'Fecha',
      dataIndex: 'fecha_ticket',
      key: 'fecha_ticket',
      width: 120,
    },
    {
      title: 'Cliente',
      dataIndex: ['cliente', 'razon'],
      key: 'cliente',
      width: 200,
    },
    {
      title: 'Productor',
      dataIndex: ['productor', 'razon'],
      key: 'productor',
      width: 200,
    },
    {
      title: 'Producto',
      dataIndex: ['producto', 'descripcion'],
      key: 'producto',
      width: 150,
    },
    {
      title: 'Kilos 1ra',
      dataIndex: 'kilos_primera',
      key: 'kilos_primera',
      width: 100,
      align: 'right',
    },
    {
      title: 'Kilos 2da',
      dataIndex: 'kilos_segunda',
      key: 'kilos_segunda',
      width: 100,
      align: 'right',
    },
    {
      title: 'Peso Neto',
      dataIndex: 'peso_neto',
      key: 'peso_neto',
      width: 100,
      align: 'right',
    },
  ];

  const rollosColumns: ColumnsType<{
    numero_ticket: number;
    rollo_identificacion: string;
    rollo_id: number;
    seleccionado: boolean;
    kilos_rollo?: number;
    desmotado?: boolean;
  }> = [
    {
      title: 'Número Ticket',
      dataIndex: 'numero_ticket',
      key: 'numero_ticket',
      width: 120,
    },
    {
      title: 'Rollo Identificación',
      dataIndex: 'rollo_identificacion',
      key: 'rollo_identificacion',
      width: 200,
      render: (text, record) => (
        <span style={{
          color: record.desmotado ? '#999' : '#000',
          fontStyle: record.desmotado ? 'italic' : 'normal',
          textDecoration: record.desmotado ? 'line-through' : 'none'
        }}>
          {text} {record.desmotado && '(Desmotado)'}
        </span>
      ),
    },
    {
      title: 'Kilos Rollo',
      dataIndex: 'kilos_rollo',
      key: 'kilos_rollo',
      width: 120,
      align: 'right',
      render: (kilos, record) => {
        // Solo mostrar kilos si el rollo está seleccionado
        const key = `${record.numero_ticket}-${record.rollo_id}`;
        if (!rollosSeleccionados[key]) {
          return '-';
        }
        
        // Calcular kilos por rollo usando la función auxiliar
        const pesaje = pesajes.find(p => p.numero_ticket === record.numero_ticket);
        if (pesaje) {
          const kilosRollo = calcularKilosPorRollo(pesaje, record.rollo_id);
          if (kilosRollo > 0) {
            return (
              <span style={{
                color: record.desmotado ? '#999' : '#000',
                fontStyle: record.desmotado ? 'italic' : 'normal'
              }}>
                {kilosRollo.toLocaleString()} kg
                {record.desmotado && ' (Desmotado)'}
              </span>
            );
          }
        }
        return '-';
      },
    },
    {
      title: 'Seleccionado',
      key: 'seleccionado',
      width: 100,
      render: (_, record) => {
        // Debug: mostrar información del rollo
        console.log(`Rollo ${record.rollo_identificacion}:`, {
          seleccionado: record.seleccionado,
          desmotado: record.desmotado,
          rollo_id: record.rollo_id
        });
        
        return (
          <input
            type="checkbox"
            checked={record.seleccionado}
            disabled={record.desmotado}
            onChange={(e) => handleSeleccionarRollo(record.numero_ticket, record.rollo_id, e.target.checked)}
            style={{
              opacity: record.desmotado ? 0.5 : 1,
              cursor: record.desmotado ? 'not-allowed' : 'pointer',
              backgroundColor: record.desmotado ? '#f5f5f5' : 'white'
            }}
            title={record.desmotado ? 'Rollo ya desmotado' : 'Seleccionar rollo'}
          />
        );
      },
    },
  ];

  useEffect(() => {
    const fetchProductos = async () => {
      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${process.env.REACT_APP_BALANZA_URL}/productos/tipo/2`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        
        const data = await response.json();
        console.log('Respuesta productos completa:', data);  // Ver la respuesta completa
        
        // Si data es directamente el array de productos
        if (Array.isArray(data)) {
          console.log('Data es un array:', data);
          setProductos(data);
           console.log('Productos cargados en estado:', data);
           console.log('Productos con nivel:', data.map(p => ({ id: p.id, descripcion: p.descripcion, nivel: p.nivel })));
        }
        // Si data tiene la estructura { status, data }
        else if (data.status === 'success' && Array.isArray(data.data)) {
          console.log('Data está dentro de data.data:', data.data);
          setProductos(data.data);
        }
        // Si data tiene otra estructura, mostrarla
        else {
          console.log('Estructura de data inesperada:', typeof data, data);
          message.error('Error en el formato de la respuesta');
        }
      } catch (error) {
        console.error('Error completo:', error);
        message.error('ERROR AL CARGAR LOS PRODUCTOS');
      }
    };

    fetchProductos();
  }, [getAccessTokenSilently]);

  const handleSubmit = async () => {
    // Verificar si el usuario está autenticado y tiene los datos necesarios
    if (!user || !id_empresa || !id_sucursal) {
      message.error('Error: Usuario no autenticado o faltan datos de empresa/sucursal');
      return;
    }

    // Verificar si al menos un ticket está cargado
    const hayAlgunTicket = [ticket1, ticket2, ticket3, ticket4].some(ticket => ticket !== '' && ticket !== '0');
    
    if (!hayAlgunTicket) {
      message.warning('POR FAVOR INGRESE AL MENOS UN NÚMERO DE TICKET');
      return;
    }

    if (!productoSeleccionado) {
      message.warning('POR FAVOR SELECCIONE UN PRODUCTO');
      return;
    }

     // Verificar que los productos estén cargados
     if (productos.length === 0) {
       message.warning('LOS PRODUCTOS AÚN NO SE HAN CARGADO. POR FAVOR ESPERE UN MOMENTO.');
      return;
    }
    
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${process.env.REACT_APP_PRODUCCION_URL}/a_produccion/${id_empresa}/${id_sucursal}/${ticket1 || '0'}/${ticket2 || '0'}/${ticket3 || '0'}/${ticket4 || '0'}/${productoSeleccionado}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = await response.json();
      if (data.status === 'success') {
        console.log('=== DATOS RECIBIDOS DEL BACKEND ===');
        console.log('Respuesta completa:', data);
        console.log('Datos de registros:', data.data);
        
        // El nuevo formato devuelve un array de registros, uno por cada rollo
        const registrosRecibidos: RegistroProduccion[] = data.data;
        
        // Filtrar registros que no estén desmotados
        const registrosDisponibles = registrosRecibidos.filter(registro => !registro.desmotado);
        
        console.log('=== REGISTROS FILTRADOS ===');
        console.log(`Total registros recibidos: ${registrosRecibidos.length}`);
        console.log(`Registros disponibles: ${registrosDisponibles.length}`);
        console.log(`Registros desmotados: ${registrosRecibidos.filter(r => r.desmotado).length}`);
        
        // Agrupar registros por ticket para mostrar en tabla
        const ticketsAgrupados = agruparRegistrosPorTicket(registrosRecibidos);
        
        setRegistros(registrosRecibidos);
        setPesajes(ticketsAgrupados);
        
        // Verificar si hay registros disponibles después del filtrado
        if (registrosDisponibles.length === 0) {
          message.warning('No hay registros disponibles para desmote. Todos los rollos están desmotados.');
          setHayResultados(false);
          setHayTicketsValidados(false);
          setTicketsValidados(['0', '0', '0', '0']);
          setRollosSeleccionados({});
          setTodosRollosSeleccionados(false);
          return;
        }
        
        // Crear array de tickets únicos validados
        const ticketsUnicos = Array.from(new Set(registrosDisponibles.map(r => r.numero_ticket)));
        console.log('🔧 ticketsUnicos:', ticketsUnicos);
        console.log('🔧 ticketsUnicos.length:', ticketsUnicos.length);
        
        // Crear array final de 4 posiciones
        const ticketsFinal = ['0', '0', '0', '0'];
        
        // Solo asignar los tickets que fueron aprobados
        ticketsUnicos.forEach((ticket: number, index: number) => {
          if (index < 4) {
            ticketsFinal[index] = ticket.toString();
          }
        });
        
        setTicketsValidados(ticketsFinal);
        setHayTicketsValidados(ticketsUnicos.length > 0);
        console.log('🔧 setHayTicketsValidados:', ticketsUnicos.length > 0, 'ticketsUnicos.length:', ticketsUnicos.length);
        setHayResultados(registrosRecibidos.length > 0);
          console.log('productos', productos);
          console.log('productoSeleccionado', productoSeleccionado);
          console.log('tipo de productoSeleccionado', typeof productoSeleccionado);
          
          // Obtener el nivel del producto directamente desde la lista de productos
          const productoEncontrado = productos.find(p => p.id === Number(productoSeleccionado));
          const nivelProducto = productoEncontrado?.nivel;
          console.log('nivel desde producto seleccionado:', nivelProducto);
          console.log('producto encontrado:', productoEncontrado);
          
          // Generar y mostrar el array de datos completos usando registrosRecibidos directamente
          const arrayDatosCompletos = generarArrayDatosCompletosDesdeRegistros(registrosRecibidos);
          console.log('=== ARRAY DE DATOS COMPLETOS ===');
          console.log('Datos de Balanza (b) + Rollos (r):', arrayDatosCompletos);
          console.log('Total de registros:', arrayDatosCompletos.length);
          console.log('Estructura de cada registro:');
          arrayDatosCompletos.forEach((registro, index) => {
            console.log(`Registro ${index + 1}:`, {
              'ID Balanza (b)': registro.id_balanza,
              'Número Ticket (b)': registro.numero_ticket,
              'Fecha (b)': registro.fecha,
              'Cliente (b)': registro.cliente,
              'Productor (b)': registro.productor,
              'Producto (b)': registro.producto,
              'Kilos (b)': registro.kilos,
              'ID Rollo (r)': registro.id_rollo,
              'Rollo Identificación (r)': registro.rollo_identificacion
            });
          });
          console.log('=== FIN ARRAY DE DATOS ===');
         
         // Inicializar rollos seleccionados para TODOS los productos (nivel 0 y nivel 1)
         const nuevosRollosSeleccionados: {[key: string]: boolean} = {};
         let rollosDisponibles = 0;
         let rollosSeleccionadosCount = 0;
         
         registrosRecibidos.forEach((registro: RegistroProduccion) => {
           // Crear clave única para cada registro (ticket + rollo_id o 'no-rollo')
           const key = `${registro.numero_ticket}-${registro.rollo_id || 'no-rollo'}`;
           
           // Solo seleccionar registros que NO estén desmotados
           if (!registro.desmotado) {
             nuevosRollosSeleccionados[key] = true; // Por defecto TRUE para todos los disponibles
             rollosSeleccionadosCount++;
           } else {
             nuevosRollosSeleccionados[key] = false; // Registros desmotados NO seleccionados
           }
           rollosDisponibles++;
         });
         
         setRollosSeleccionados(nuevosRollosSeleccionados);
         // Marcar como "todos seleccionados" si hay registros disponibles y todos están seleccionados
         setTodosRollosSeleccionados(rollosDisponibles > 0 && rollosSeleccionadosCount === rollosDisponibles);
         
         console.log('=== INICIALIZACIÓN DE CHECKBOXES ===');
         console.log(`Total registros: ${rollosDisponibles}`);
         console.log(`Registros seleccionados: ${rollosSeleccionadosCount}`);
         console.log(`Registros desmotados: ${rollosDisponibles - rollosSeleccionadosCount}`);
         console.log('Estado de rollosSeleccionados:', nuevosRollosSeleccionados);
         console.log('todosRollosSeleccionados:', rollosDisponibles > 0 && rollosSeleccionadosCount === rollosDisponibles);
        
        message.success(data.message);
      } else {
        message.error(data.message);
        setHayResultados(false);
        setHayTicketsValidados(false);
        setTicketsValidados(['0', '0', '0', '0']);
        setRollosSeleccionados({});
        setTodosRollosSeleccionados(false);
      }
    } catch (error) {
      console.error('Error:', error);
      message.error('ERROR AL PROCESAR LOS TICKETS');
      setHayResultados(false);
      setHayTicketsValidados(false);
      setTicketsValidados(['0', '0', '0', '0']);
      setRollosSeleccionados({});
      setTodosRollosSeleccionados(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDesmotar = () => {
    console.log(`=== INICIANDO DESMOTAR ===`);
    console.log(`Estado de rollosSeleccionados:`, rollosSeleccionados);
    console.log(`Estado de todosRollosSeleccionados:`, todosRollosSeleccionados);
    console.log(`Registros disponibles:`, registros);
    console.log(`hayticketsvalidos:`, hayTicketsValidados);
    console.log(`productoSeleccionado:`, productoSeleccionado);
    console.log(`productos:`, productos);
    console.log(`registros.length:`, registros.length);
    console.log(`registros:`, registros);
    
    if (!hayTicketsValidados) {
      message.warning('NO HAY TICKETS VALIDADOS PARA DESMOTAR');
      console.log('❌ SALIENDO DE DESMOTAR - No hay tickets validados');
      return;
    }
    
    console.log('✅ CONTINUANDO DESMOTAR - Hay tickets validados');
    
    // Obtener el nivel del producto para determinar si hay rollos
    const productoEncontrado = productos.find(p => p.id === Number(productoSeleccionado));
    const nivelProducto = productoEncontrado?.nivel;

    // Si todosRollosSeleccionados es true, asegurar que todos los rollos DISPONIBLES estén seleccionados
    const rollosSeleccionadosFinal = { ...rollosSeleccionados };
    if (todosRollosSeleccionados) {
      console.log(`Todos los rollos disponibles están seleccionados, asegurando estado...`);
      registros.forEach(registro => {
        if (registro.rollo_id) {
          const key = `${registro.numero_ticket}-${registro.rollo_id}`;
          // Solo seleccionar rollos que NO estén desmotados
          if (!registro.desmotado) {
            rollosSeleccionadosFinal[key] = true;
            console.log(`  Rollo ${registro.rollo_identificacion} (${registro.rollo_id}): true (disponible)`);
          } else {
            rollosSeleccionadosFinal[key] = false; // Rollos desmotados siempre NO seleccionados
            console.log(`  Rollo ${registro.rollo_identificacion} (${registro.rollo_id}): false (desmotado)`);
          }
        }
      });
      console.log(`Estado final de rollosSeleccionados:`, rollosSeleccionadosFinal);
    }

    // Recopilar datos de registros seleccionados
    const datosSeleccionados = registros
      .filter(registro => {
        // Solo incluir registros que estén seleccionados y no desmotados
        const key = `${registro.numero_ticket}-${registro.rollo_id || 'no-rollo'}`;
        return rollosSeleccionadosFinal[key] && !registro.desmotado;
      })
      .map(registro => {
        const ticketData: {
          id_balanza: number;
          numero_ticket: number;
          fecha: string;
          cliente: string;
          productor: string;
          kilos: number;
          rollos: Array<{
            id_rollo: number | null;
            rollo_identificacion: string | null;
          }>;
        } = {
          id_balanza: registro.id,
          numero_ticket: registro.numero_ticket,
          fecha: registro.fecha,
          cliente: registro.cliente.razon,
          productor: registro.productor.razon,
          kilos: registro.peso_neto,
          rollos: []
        };

        // Agregar rollo para todos los productos (nivel 0 y nivel 1)
        if (registro.rollo_id) {
          // Si hay rollo_id, usar los datos reales del rollo
          console.log(`=== PROCESANDO REGISTRO ${registro.numero_ticket} CON ROLLO ${registro.rollo_id} ===`);
          console.log(`Producto nivel: ${nivelProducto}, Rollo ID: ${registro.rollo_id}, Rollo Identificación: ${registro.rollo_identificacion}`);
          
          ticketData.rollos.push({
            id_rollo: registro.rollo_id,
            rollo_identificacion: registro.rollo_identificacion
          });
          console.log(`✅ Rollo ${registro.rollo_identificacion} agregado al array de datos (Nivel ${nivelProducto})`);
        } else {
          // Si no hay rollo_id, agregar un rollo vacío
          console.log(`=== PROCESANDO REGISTRO ${registro.numero_ticket} SIN ROLLOS ===`);
          console.log(`Producto nivel: ${nivelProducto}, Sin Rollo ID`);
          
          ticketData.rollos.push({
            id_rollo: null,
            rollo_identificacion: null
          });
          
          console.log(`✅ Registro ${registro.numero_ticket} agregado con rollo vacío (null) - Nivel ${nivelProducto}`);
        }

        return ticketData;
      });

    // Filtrar solo los tickets que tienen datos válidos
    console.log('=== FILTRANDO TICKETS CON DATOS VÁLIDOS ===');
    console.log('datosSeleccionados:', datosSeleccionados);
    console.log('datosSeleccionados.length:', datosSeleccionados.length);
    
    const ticketsConDatos = datosSeleccionados.filter(ticket => 
      ticket.numero_ticket && ticket.numero_ticket !== 0
    );

    console.log('ticketsConDatos después del filtro:', ticketsConDatos);
    console.log('ticketsConDatos.length:', ticketsConDatos.length);

    if (ticketsConDatos.length === 0) {
      message.warning('NO HAY TICKETS CON DATOS VÁLIDOS PARA DESMOTAR');
      console.log('❌ SALIENDO DE DESMOTAR - No hay tickets con datos válidos');
      return;
    }
    
    console.log('✅ CONTINUANDO DESMOTAR - Hay tickets con datos válidos');

    // Validar que no se mezclen tickets de Compra (C) y Servicio (S) en la misma producción
    const tiposCS = new Set(
      registros
        .filter(r => {
          const key = `${r.numero_ticket}-${r.rollo_id || 'no-rollo'}`;
          return rollosSeleccionadosFinal[key] && !r.desmotado;
        })
        .map(r => r.compra_servicio)
    );
    if (tiposCS.has('C') && tiposCS.has('S')) {
      message.error('No se pueden mezclar tickets de Compra y Servicio en la misma producción. Seleccione solo uno u otro tipo.');
      console.log('❌ SALIENDO DE DESMOTAR - Mezcla de tickets Compra y Servicio detectada');
      return;
    }

         // Generar el array de datos completos usando la nueva estrategia (rollo_id=0, rollo_identificacion="")
     console.log('=== ANTES DE GENERAR ARRAY DATOS COMPLETOS ===');
     console.log('pesajes:', pesajes);
     console.log('pesajes.length:', pesajes.length);
     console.log('ticketsConDatos:', ticketsConDatos);
     console.log('ticketsConDatos.length:', ticketsConDatos.length);
     
              // Generar array de datos completos basado en los registros seleccionados
     let arrayDatosCompletos: any[] = [];
     
     if (datosSeleccionados.length > 0) {
       console.log('Generando array de datos completos desde registros seleccionados');
       arrayDatosCompletos = datosSeleccionados.map(registro => {
         // Buscar el registro original en registros para obtener los IDs
         const registroOriginal = registros.find(r => r.id === registro.id_balanza);
         
         if (!registroOriginal) {
           console.error(`No se encontró registro original para id_balanza: ${registro.id_balanza}`);
           throw new Error(`No se encontró registro original para ticket ${registro.numero_ticket}`);
         }
         
         const clienteId = registroOriginal.cliente?.id;
         const productorId = registroOriginal.productor?.id;
         
         if (!clienteId || !productorId) {
           console.error(`IDs faltantes para ticket ${registro.numero_ticket}: cliente_id=${clienteId}, productor_id=${productorId}`);
           throw new Error(`IDs faltantes para ticket ${registro.numero_ticket}`);
         }
         
         return {
           // Datos de Balanza (b)
           id_balanza: registro.id_balanza,
           numero_ticket: registro.numero_ticket,
           fecha: registro.fecha,
           cliente: registro.cliente,
           cliente_id: clienteId,
           productor: registro.productor,
           productor_id: productorId,
           producto: registroOriginal.producto?.descripcion || '',
           kilos: registro.kilos,
           compra_servicio: registroOriginal.compra_servicio || 'S',
           // Datos de Rollos (r)
           id_rollo: registro.rollos[0]?.id_rollo || null,
           rollo_identificacion: registro.rollos[0]?.rollo_identificacion || null,
           presentacion_id: registroOriginal.presentacion || null
         };
       });
     } else {
       console.log('No hay registros seleccionados para generar array de datos completos');
     }
     
     // Guardar datos en el contexto
     const produccionData = {
       tickets: datosSeleccionados,
       productoId: productoSeleccionado!,
       productoNivel: nivelProducto || '0',
       arrayDatosCompletos
     };

     console.log('=== RESUMEN FINAL DE DATOS A ENVIAR ===');
     console.log('Registros seleccionados:', datosSeleccionados);
     datosSeleccionados.forEach((registro, index) => {
       console.log(`Registro ${index + 1} (Ticket ${registro.numero_ticket}):`);
       console.log(`  - Rollos a enviar: ${registro.rollos.length}`);
       registro.rollos.forEach((rollo, rolloIndex) => {
         console.log(`    Rollo ${rolloIndex + 1}: ${rollo.rollo_identificacion} (ID: ${rollo.id_rollo})`);
       });
     });
     console.log('Array de datos completos:', arrayDatosCompletos);
     console.log('Total de registros en arrayDatosCompletos:', arrayDatosCompletos.length);
     console.log('=== FIN RESUMEN ===');

         console.log('Antes de setProduccionData - produccionData:', produccionData);
     setProduccionData(produccionData);
     console.log('Después de setProduccionData - datos guardados en contexto');
     console.log('Verificando datos antes de navegar:', {
       registrosLength: produccionData.tickets.length,
       arrayDatosCompletosLength: produccionData.arrayDatosCompletos.length,
       productoId: produccionData.productoId,
       productoNivel: produccionData.productoNivel
     });
     
     // Log adicional para verificar el envío de datos
     console.log('=== ENVÍO DE DATOS ---> ===');
     console.log('Datos completos a enviar:', {
       registros: produccionData.tickets,
       productoId: produccionData.productoId,
       productoNivel: produccionData.productoNivel,
       arrayDatosCompletos: produccionData.arrayDatosCompletos
     });
     console.log('Estructura de registros enviados:');
     produccionData.tickets.forEach((registro, index) => {
       console.log(`Registro ${index + 1}:`, {
         id_balanza: registro.id_balanza,
         numero_ticket: registro.numero_ticket,
         fecha: registro.fecha,
         cliente: registro.cliente,
         productor: registro.productor,
         kilos: registro.kilos,
         rollos: registro.rollos
       });
     });
     console.log('=== FIN ENVÍO DE DATOS ===');
     
     // Mostrar el array de datos completos antes de navegar
     console.log('=== ARRAY DE DATOS COMPLETOS (DESMOTAR) ===');
     console.log('Datos de Balanza (b) + Rollos (r):', arrayDatosCompletos);
     console.log('Total de registros:', arrayDatosCompletos.length);
     console.log('Estructura de cada registro:');
     arrayDatosCompletos.forEach((registro: any, index: number) => {
       console.log(`Registro ${index + 1}:`, {
         'ID Balanza (b)': registro.id_balanza,
         'Número Ticket (b)': registro.numero_ticket,
         'Fecha (b)': registro.fecha,
         'Cliente (b)': registro.cliente,
         'Productor (b)': registro.productor,
         'Producto (b)': registro.producto,
         'Kilos (b)': registro.kilos,
         'ID Rollo (r)': registro.id_rollo,
         'Rollo Identificación (r)': registro.rollo_identificacion
       });
     });
     console.log('=== FIN ARRAY DE DATOS (DESMOTAR) ===');
     
     // Pequeño delay para asegurar que los datos se guarden en el contexto
     setTimeout(() => {
       // Navegar a ProduccionLayout
       navigate('/produccion_fardos');
     }, 100);
  };

     // Función para manejar el cambio de producto seleccionado
   const handleProductoChange = (productoId: string) => {
     setProductoSeleccionado(productoId);
     
     // Buscar el producto completo en la lista
     const productoEncontrado = productos.find(p => p.id === Number(productoId));
     setProductoCompleto(productoEncontrado || null);
   };

  // Función para manejar la selección de todos los registros
  const handleSeleccionarTodosRegistros = (checked: boolean) => {
    const nuevosRollosSeleccionados: {[key: string]: boolean} = {};
    
    console.log(`=== SELECCIONANDO TODOS LOS REGISTROS ===`);
    console.log(`Estado solicitado: ${checked}`);
    console.log(`Registros disponibles:`, registros);
    
    registros.forEach(registro => {
      const key = `${registro.numero_ticket}-${registro.rollo_id || 'no-rollo'}`;
      // Solo seleccionar registros que NO estén desmotados
      if (!registro.desmotado) {
        nuevosRollosSeleccionados[key] = checked;
        console.log(`  Registro ${registro.numero_ticket} - ${registro.rollo_identificacion}: ${checked} (disponible)`);
      } else {
        nuevosRollosSeleccionados[key] = false; // Registros desmotados siempre NO seleccionados
        console.log(`  Registro ${registro.numero_ticket} - ${registro.rollo_identificacion}: false (desmotado)`);
      }
    });
    
    console.log(`Estado final de rollosSeleccionados:`, nuevosRollosSeleccionados);
    console.log(`=== FIN SELECCIONANDO TODOS LOS REGISTROS ===`);
    
    setRollosSeleccionados(nuevosRollosSeleccionados);
    setTodosRollosSeleccionados(checked);
  };

  // Función para manejar la selección de todos los rollos (mantener para compatibilidad)
  const handleSeleccionarTodosRollos = (checked: boolean) => {
    const nuevosRollosSeleccionados: {[key: string]: boolean} = {};
    
    console.log(`=== SELECCIONANDO TODOS LOS ROLLOS ===`);
    console.log(`Estado solicitado: ${checked}`);
    console.log(`Registros disponibles:`, registros);
    
    registros.forEach(registro => {
      if (registro.rollo_id) {
        const key = `${registro.numero_ticket}-${registro.rollo_id}`;
        // Solo seleccionar rollos que NO estén desmotados
        if (!registro.desmotado) {
          nuevosRollosSeleccionados[key] = checked;
          console.log(`  Rollo ${registro.rollo_identificacion} (${registro.rollo_id}): ${checked} (disponible)`);
        } else {
          nuevosRollosSeleccionados[key] = false; // Rollos desmotados siempre NO seleccionados
          console.log(`  Rollo ${registro.rollo_identificacion} (${registro.rollo_id}): false (desmotado)`);
        }
      }
    });
    
    console.log(`Estado final de rollosSeleccionados:`, nuevosRollosSeleccionados);
    console.log(`=== FIN SELECCIONANDO TODOS LOS ROLLOS ===`);
    
    setRollosSeleccionados(nuevosRollosSeleccionados);
    setTodosRollosSeleccionados(checked);
  };

  // Función para manejar la selección individual de registros
  const handleSeleccionarRegistro = (registro: RegistroProduccion, checked: boolean) => {
    // Verificar si el registro está desmotado
    if (registro.desmotado) {
      console.log(`=== REGISTRO DESMOTADO - NO SE PUEDE SELECCIONAR ===`);
      console.log(`Registro ${registro.numero_ticket} - ${registro.rollo_identificacion} está desmotado, no se puede seleccionar`);
      console.log(`=== FIN REGISTRO DESMOTADO ===`);
      return; // No permitir seleccionar registros desmotados
    }
    
    const key = `${registro.numero_ticket}-${registro.rollo_id || 'no-rollo'}`;
    const nuevosRollosSeleccionados = { ...rollosSeleccionados };
    nuevosRollosSeleccionados[key] = checked;
    
    console.log(`=== SELECCIONANDO REGISTRO ===`);
    console.log(`Ticket: ${registro.numero_ticket}, Rollo: ${registro.rollo_identificacion}, Seleccionado: ${checked}`);
    console.log(`Key: ${key}`);
    console.log(`Estado anterior: ${rollosSeleccionados[key]}`);
    console.log(`Estado nuevo: ${checked}`);
    console.log(`Estado completo actualizado:`, nuevosRollosSeleccionados);
    
    setRollosSeleccionados(nuevosRollosSeleccionados);
    
    // Verificar si todos los registros disponibles están seleccionados
    const totalRegistrosDisponibles = registros.filter(r => !r.desmotado).length;
    const registrosSeleccionadosCount = Object.values(nuevosRollosSeleccionados).filter(Boolean).length;
    
    console.log(`Total registros disponibles: ${totalRegistrosDisponibles}, Registros seleccionados: ${registrosSeleccionadosCount}`);
    console.log(`=== FIN SELECCIONANDO REGISTRO ===`);
    
    setTodosRollosSeleccionados(totalRegistrosDisponibles > 0 && registrosSeleccionadosCount === totalRegistrosDisponibles);
  };

  // Función para manejar la selección individual de rollos (mantener para compatibilidad)
  const handleSeleccionarRollo = (numeroTicket: number, rolloId: number, checked: boolean) => {
    // Verificar si el rollo está desmotado
    const registro = registros.find(r => r.numero_ticket === numeroTicket && r.rollo_id === rolloId);
    
    if (registro?.desmotado) {
      console.log(`=== ROLLO DESMOTADO - NO SE PUEDE SELECCIONAR ===`);
      console.log(`Rollo ${registro.rollo_identificacion} (${rolloId}) está desmotado, no se puede seleccionar`);
      console.log(`=== FIN ROLLO DESMOTADO ===`);
      return; // No permitir seleccionar rollos desmotados
    }
    
    const key = `${numeroTicket}-${rolloId}`;
    const nuevosRollosSeleccionados = { ...rollosSeleccionados };
    nuevosRollosSeleccionados[key] = checked;
    
    console.log(`=== SELECCIONANDO ROLLO ===`);
    console.log(`Ticket: ${numeroTicket}, Rollo ID: ${rolloId}, Seleccionado: ${checked}`);
    console.log(`Key: ${key}`);
    console.log(`Estado anterior: ${rollosSeleccionados[key]}`);
    console.log(`Estado nuevo: ${checked}`);
    console.log(`Estado completo actualizado:`, nuevosRollosSeleccionados);
    
    setRollosSeleccionados(nuevosRollosSeleccionados);
    
    // Verificar si todos los rollos disponibles están seleccionados
    const totalRollosDisponibles = registros.filter(r => r.rollo_id && !r.desmotado).length;
    const rollosSeleccionadosCount = Object.values(nuevosRollosSeleccionados).filter(Boolean).length;
    
    console.log(`Total rollos disponibles: ${totalRollosDisponibles}, Rollos seleccionados: ${rollosSeleccionadosCount}`);
    console.log(`=== FIN SELECCIONANDO ROLLO ===`);
    
    setTodosRollosSeleccionados(totalRollosDisponibles > 0 && rollosSeleccionadosCount === totalRollosDisponibles);
  };

  // Función auxiliar para calcular kilos por rollo (ya no necesaria con el nuevo formato)
  const calcularKilosPorRollo = (pesaje: Pesaje, rolloId: number) => {
    // En el nuevo formato, los kilos ya vienen calculados por rollo
    const registro = registros.find(r => r.numero_ticket === pesaje.numero_ticket && r.rollo_id === rolloId);
    return registro ? registro.peso_neto : 0;
  };

  // Función para obtener todos los rollos de todos los registros
  const obtenerTodosLosRollos = () => {
    const todosLosRollos: Array<{
      numero_ticket: number;
      rollo_identificacion: string;
      rollo_id: number;
      seleccionado: boolean;
      desmotado?: boolean;
    }> = [];
    
    console.log('=== OBTENIENDO TODOS LOS ROLLOS ===');
    console.log('Registros disponibles:', registros);
    
    registros.forEach(registro => {
      if (registro.rollo_id) {
        console.log(`Procesando registro ${registro.numero_ticket}:`);
        const key = `${registro.numero_ticket}-${registro.rollo_id}`;
        const rolloData = {
          numero_ticket: registro.numero_ticket,
          rollo_identificacion: registro.rollo_identificacion,
          rollo_id: registro.rollo_id,
          seleccionado: rollosSeleccionados[key] || false,
          desmotado: registro.desmotado || false
        };
        
        console.log(`  Rollo ${registro.rollo_identificacion}:`, {
          key,
          seleccionado: rolloData.seleccionado,
          desmotado: rolloData.desmotado,
          registroOriginal: registro
        });
        
        todosLosRollos.push(rolloData);
      }
    });
    
    console.log('Rollos procesados:', todosLosRollos);
    console.log('=== FIN OBTENIENDO ROLLOS ===');
    
    return todosLosRollos;
  };

  // Función para generar array con datos de tickets y rollos
  const generarArrayDatosCompletos = (estadoRollos = rollosSeleccionados) => {
    const arrayDatos: Array<{
      // Datos de Balanza (b)
      id_balanza: number;
      numero_ticket: number;
      fecha: string;
      cliente: string;
      cliente_id: number;
      productor: string;
      productor_id: number;
      producto: string;
      kilos: number;
      // Datos de Rollos (r)
      id_rollo: number | null;
      rollo_identificacion: string | null;
    }> = [];

    console.log('=== GENERANDO ARRAY DATOS COMPLETOS ===');
    console.log('Pesajes disponibles:', pesajes);
    console.log('Estado de rollos:', estadoRollos);

    // Usar pesajes directamente
    console.log('Tickets a procesar:', pesajes);

    pesajes.forEach((pesaje: Pesaje) => {
      console.log(`Procesando ticket ${pesaje.numero_ticket} con ${pesaje.rollos?.length || 0} rollos`);
      
      // Si el producto es nivel 1 y hay rollos
      if (pesaje.rollos && pesaje.rollos.length > 0) {
        console.log(`=== GENERANDO ARRAY DATOS COMPLETOS - Ticket ${pesaje.numero_ticket} CON ROLLOS ===`);
        
        // Filtrar solo los rollos seleccionados que NO estén desmotados
        const rollosSeleccionadosDelTicket = pesaje.rollos.filter(rollo => {
          const key = `${pesaje.numero_ticket}-${rollo.id}`;
          const estaSeleccionado = estadoRollos[key];
          const noEstaDesmotado = !rollo.desmotado;
          console.log(`Rollo ${rollo.rollo_identificacion} (${rollo.id}): seleccionado = ${estaSeleccionado}, desmotado = ${rollo.desmotado}`);
          return estaSeleccionado && noEstaDesmotado; // Solo rollos seleccionados Y no desmotados
        });

        if (rollosSeleccionadosDelTicket.length > 0) {
          console.log(`=== PROCESANDO ROLLOS SELECCIONADOS - Ticket ${pesaje.numero_ticket} ===`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Total de rollos en el ticket: ${pesaje.rollos.length}`);
          console.log(`Cantidad de rollos SELECCIONADOS: ${rollosSeleccionadosDelTicket.length}`);
          console.log(`Rollos seleccionados:`, rollosSeleccionadosDelTicket.map(r => r.rollo_identificacion));

          rollosSeleccionadosDelTicket.forEach((rollo) => {
            const kilosRollo = calcularKilosPorRollo(pesaje, rollo.id);
            console.log(`Rollo ${rollo.rollo_identificacion}: ${kilosRollo} kg`);

            arrayDatos.push({
              // Datos de Balanza (b)
              id_balanza: pesaje.id,
              numero_ticket: pesaje.numero_ticket,
              fecha: pesaje.fecha_ticket,
              cliente: pesaje.cliente.razon,
              cliente_id: pesaje.cliente.id,
              productor: pesaje.productor.razon,
              productor_id: pesaje.productor.id,
              producto: pesaje.producto.descripcion,
              kilos: kilosRollo, // Kilos calculados por rollo (fijo por total de rollos)
              // Datos de Rollos (r)
              id_rollo: rollo.id,
              rollo_identificacion: rollo.rollo_identificacion
            });
          });

          // Verificar que la suma sea correcta (solo de rollos seleccionados)
          const sumaCalculada = arrayDatos
            .filter(item => item.numero_ticket === pesaje.numero_ticket)
            .reduce((sum, item) => sum + item.kilos, 0);
          console.log(`Suma total de kilos por rollos seleccionados: ${sumaCalculada} kg`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Diferencia: ${pesaje.peso_neto - sumaCalculada} kg`);
          console.log('=== FIN PROCESAMIENTO ROLLOS ===');
        }
      } else {
        // Si no hay rollos, agregar solo los datos de balanza con rollo null
        console.log(`=== PROCESANDO TICKET ${pesaje.numero_ticket} SIN ROLLOS (GRANEL) ===`);
        console.log(`Kilos del ticket: ${pesaje.peso_neto} kg`);
        
        arrayDatos.push({
          // Datos de Balanza (b)
          id_balanza: pesaje.id,
          numero_ticket: pesaje.numero_ticket,
          fecha: pesaje.fecha_ticket,
          cliente: pesaje.cliente.razon,
          cliente_id: pesaje.cliente.id,
          productor: pesaje.productor.razon,
          productor_id: pesaje.productor.id,
          producto: pesaje.producto.descripcion,
          kilos: pesaje.peso_neto,
          // Datos de Rollos (r) - null para tickets sin rollos
          id_rollo: null,
          rollo_identificacion: null
        });
        
        console.log(`✅ Ticket ${pesaje.numero_ticket} agregado al array con rollo null`);
      }
    });

    console.log(`=== RESULTADO FINAL ===`);
    console.log(`Total de registros generados: ${arrayDatos.length}`);
    console.log(`Array de datos:`, arrayDatos);
    
    return arrayDatos;
  };

  // Función para generar array con datos de tickets y rollos usando datos específicos
  const generarArrayDatosCompletosConDatos = (ticketsData: Pesaje[]) => {
    const arrayDatos: Array<{
      // Datos de Balanza (b)
      id_balanza: number;
      numero_ticket: number;
      fecha: string;
      cliente: string;
      cliente_id: number;
      productor: string;
      productor_id: number;
      producto: string;
      kilos: number;
      // Datos de Rollos (r)
      id_rollo: number | null;
      rollo_identificacion: string | null;
    }> = [];

    console.log('=== GENERANDO ARRAY DATOS COMPLETOS CON DATOS ESPECÍFICOS ===');
    console.log('Tickets data recibidos:', ticketsData);
    console.log('Estado de rollos:', rollosSeleccionados);

    ticketsData.forEach((pesaje: Pesaje) => {
      console.log(`Procesando ticket ${pesaje.numero_ticket} con ${pesaje.rollos?.length || 0} rollos`);
      
      // Si el producto es nivel 1 y hay rollos
      if (pesaje.rollos && pesaje.rollos.length > 0) {
        console.log(`=== GENERANDO ARRAY DATOS COMPLETOS - Ticket ${pesaje.numero_ticket} CON ROLLOS ===`);
        
        // Filtrar solo los rollos seleccionados que NO estén desmotados
        const rollosSeleccionadosDelTicket = pesaje.rollos.filter(rollo => {
          const key = `${pesaje.numero_ticket}-${rollo.id}`;
          const estaSeleccionado = rollosSeleccionados[key];
          const noEstaDesmotado = !rollo.desmotado;
          console.log(`Rollo ${rollo.rollo_identificacion} (${rollo.id}): seleccionado = ${estaSeleccionado}, desmotado = ${rollo.desmotado}`);
          return estaSeleccionado && noEstaDesmotado; // Solo rollos seleccionados Y no desmotados
        });

        if (rollosSeleccionadosDelTicket.length > 0) {
          console.log(`=== PROCESANDO ROLLOS SELECCIONADOS - Ticket ${pesaje.numero_ticket} ===`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Total de rollos en el ticket: ${pesaje.rollos.length}`);
          console.log(`Cantidad de rollos SELECCIONADOS: ${rollosSeleccionadosDelTicket.length}`);
          console.log(`Rollos seleccionados:`, rollosSeleccionadosDelTicket.map(r => r.rollo_identificacion));

          rollosSeleccionadosDelTicket.forEach((rollo) => {
            const kilosRollo = calcularKilosPorRollo(pesaje, rollo.id);
            console.log(`Rollo ${rollo.rollo_identificacion}: ${kilosRollo} kg`);

            arrayDatos.push({
              // Datos de Balanza (b)
              id_balanza: pesaje.id,
              numero_ticket: pesaje.numero_ticket,
              fecha: pesaje.fecha_ticket,
              cliente: pesaje.cliente.razon,
              cliente_id: pesaje.cliente.id,
              productor: pesaje.productor.razon,
              productor_id: pesaje.productor.id,
              producto: pesaje.producto.descripcion,
              kilos: kilosRollo, // Kilos calculados por rollo (fijo por total de rollos)
              // Datos de Rollos (r)
              id_rollo: rollo.id,
              rollo_identificacion: rollo.rollo_identificacion
            });
          });

          // Verificar que la suma sea correcta (solo de rollos seleccionados)
          const sumaCalculada = arrayDatos
            .filter(item => item.numero_ticket === pesaje.numero_ticket)
            .reduce((sum, item) => sum + item.kilos, 0);
          console.log(`Suma total de kilos por rollos seleccionados: ${sumaCalculada} kg`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Diferencia: ${pesaje.peso_neto - sumaCalculada} kg`);
          console.log('=== FIN PROCESAMIENTO ROLLOS ===');
        }
      } else {
        // Si no hay rollos, agregar solo los datos de balanza con rollo null
        console.log(`=== PROCESANDO TICKET ${pesaje.numero_ticket} SIN ROLLOS (GRANEL) ===`);
        console.log(`Kilos del ticket: ${pesaje.peso_neto} kg`);
        
        arrayDatos.push({
          // Datos de Balanza (b)
          id_balanza: pesaje.id,
          numero_ticket: pesaje.numero_ticket,
          fecha: pesaje.fecha_ticket,
          cliente: pesaje.cliente.razon,
          cliente_id: pesaje.cliente.id,
          productor: pesaje.productor.razon,
          productor_id: pesaje.productor.id,
          producto: pesaje.producto.descripcion,
          kilos: pesaje.peso_neto,
          // Datos de Rollos (r) - null para tickets sin rollos
          id_rollo: null,
          rollo_identificacion: null
        });
        
        console.log(`✅ Ticket ${pesaje.numero_ticket} agregado al array con rollo null`);
      }
    });

    console.log(`=== RESULTADO FINAL ===`);
    console.log(`Total de registros generados: ${arrayDatos.length}`);
    console.log(`Array de datos:`, arrayDatos);
    
    return arrayDatos;
  };

  // Función para generar array con datos de tickets y rollos usando la nueva estrategia (rollo_id=0, rollo_identificacion="")
  const generarArrayDatosCompletosNuevaEstrategia = (ticketsData: Pesaje[]) => {
    const arrayDatos: Array<{
      // Datos de Balanza (b)
      id_balanza: number;
      numero_ticket: number;
      fecha: string;
      cliente: string;
      cliente_id: number;
      productor: string;
      productor_id: number;
      producto: string;
      kilos: number;
      // Datos de Rollos (r)
      id_rollo: number;
      rollo_identificacion: string;
    }> = [];

    console.log('=== GENERANDO ARRAY DATOS COMPLETOS NUEVA ESTRATEGIA ===');
    console.log('Tickets data recibidos:', ticketsData);
    console.log('Estado de rollos:', rollosSeleccionados);

    ticketsData.forEach((pesaje: Pesaje) => {
      console.log(`Procesando ticket ${pesaje.numero_ticket} con ${pesaje.rollos?.length || 0} rollos`);
      
      // Si el producto es nivel 1 y hay rollos
      if (pesaje.rollos && pesaje.rollos.length > 0) {
        console.log(`=== GENERANDO ARRAY DATOS COMPLETOS - Ticket ${pesaje.numero_ticket} CON ROLLOS ===`);
        
        // Filtrar solo los rollos seleccionados que NO estén desmotados
        const rollosSeleccionadosDelTicket = pesaje.rollos.filter(rollo => {
          const key = `${pesaje.numero_ticket}-${rollo.id}`;
          const estaSeleccionado = rollosSeleccionados[key];
          const noEstaDesmotado = !rollo.desmotado;
          console.log(`Rollo ${rollo.rollo_identificacion} (${rollo.id}): seleccionado = ${estaSeleccionado}, desmotado = ${rollo.desmotado}`);
          return estaSeleccionado && noEstaDesmotado; // Solo rollos seleccionados Y no desmotados
        });

        if (rollosSeleccionadosDelTicket.length > 0) {
          console.log(`=== PROCESANDO ROLLOS SELECCIONADOS - Ticket ${pesaje.numero_ticket} ===`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Total de rollos en el ticket: ${pesaje.rollos.length}`);
          console.log(`Cantidad de rollos SELECCIONADOS: ${rollosSeleccionadosDelTicket.length}`);
          console.log(`Rollos seleccionados:`, rollosSeleccionadosDelTicket.map(r => r.rollo_identificacion));

          rollosSeleccionadosDelTicket.forEach((rollo) => {
            const kilosRollo = calcularKilosPorRollo(pesaje, rollo.id);
            console.log(`Rollo ${rollo.rollo_identificacion}: ${kilosRollo} kg`);

            arrayDatos.push({
              // Datos de Balanza (b)
              id_balanza: pesaje.id,
              numero_ticket: pesaje.numero_ticket,
              fecha: pesaje.fecha_ticket,
              cliente: pesaje.cliente.razon,
              cliente_id: pesaje.cliente.id,
              productor: pesaje.productor.razon,
              productor_id: pesaje.productor.id,
              producto: pesaje.producto.descripcion,
              kilos: kilosRollo, // Kilos calculados por rollo (fijo por total de rollos)
              // Datos de Rollos (r)
              id_rollo: rollo.id,
              rollo_identificacion: rollo.rollo_identificacion
            });
          });

          // Verificar que la suma sea correcta (solo de rollos seleccionados)
          const sumaCalculada = arrayDatos
            .filter(item => item.numero_ticket === pesaje.numero_ticket)
            .reduce((sum, item) => sum + item.kilos, 0);
          console.log(`Suma total de kilos por rollos seleccionados: ${sumaCalculada} kg`);
          console.log(`Kilos totales del ticket: ${pesaje.peso_neto}`);
          console.log(`Diferencia: ${pesaje.peso_neto - sumaCalculada} kg`);
          console.log('=== FIN PROCESAMIENTO ROLLOS ===');
        }
      } else {
        // Si no hay rollos, simular el ticket como un rollo con rollo_id=0 y rollo_identificacion vacío
        console.log(`=== PROCESANDO TICKET ${pesaje.numero_ticket} SIN ROLLOS (GRANEL) ===`);
        console.log(`Kilos del ticket: ${pesaje.peso_neto} kg`);
        console.log(`Simulando ticket como rollo con rollo_id=0 y rollo_identificacion=""`);
        
        arrayDatos.push({
          // Datos de Balanza (b)
          id_balanza: pesaje.id,
          numero_ticket: pesaje.numero_ticket,
          fecha: pesaje.fecha_ticket,
          cliente: pesaje.cliente.razon,
          cliente_id: pesaje.cliente.id,
          productor: pesaje.productor.razon,
          productor_id: pesaje.productor.id,
          producto: pesaje.producto.descripcion,
          kilos: pesaje.peso_neto,
          // Datos de Rollos (r) - simulando ticket como rollo
          id_rollo: 0,
          rollo_identificacion: ""
        });
        
        console.log(`✅ Ticket ${pesaje.numero_ticket} agregado al array simulando como rollo (rollo_id=0, rollo_identificacion="")`);
      }
    });

    console.log(`=== RESULTADO FINAL ===`);
    console.log(`Total de registros generados: ${arrayDatos.length}`);
    console.log(`Array de datos:`, arrayDatos);
    
    return arrayDatos;
  };

  // Función para generar array de datos completos desde registros
  const generarArrayDatosCompletosDesdeRegistros = (registrosData: RegistroProduccion[]) => {
    const arrayDatos: Array<{
      // Datos de Balanza (b)
      id_balanza: number;
      numero_ticket: number;
      fecha: string;
      cliente: string;
      cliente_id: number;
      productor: string;
      productor_id: number;
      producto: string;
      kilos: number;
      // Datos de Rollos (r)
      id_rollo: number | null;
      rollo_identificacion: string | null;
    }> = [];

    console.log('=== GENERANDO ARRAY DATOS COMPLETOS DESDE REGISTROS ===');
    console.log('Registros data recibidos:', registrosData);

    registrosData.forEach((registro: RegistroProduccion) => {
      console.log(`Procesando registro: Ticket ${registro.numero_ticket}, Rollo: ${registro.rollo_identificacion || 'Sin rollo'}`);
      
      arrayDatos.push({
        // Datos de Balanza (b)
        id_balanza: registro.id,
        numero_ticket: registro.numero_ticket,
        fecha: registro.fecha,
        cliente: registro.cliente.razon,
        cliente_id: registro.cliente.id,
        productor: registro.productor.razon,
        productor_id: registro.productor.id,
        producto: registro.producto.descripcion,
        kilos: registro.peso_neto,
        // Datos de Rollos (r)
        id_rollo: registro.rollo_id,
        rollo_identificacion: registro.rollo_identificacion || null
      });
    });

    console.log(`=== RESULTADO FINAL ===`);
    console.log(`Total de registros generados: ${arrayDatos.length}`);
    console.log(`Array de datos:`, arrayDatos);
    
    return arrayDatos;
  };

  // Función para generar array de datos completos desde ticketsConDatos
  const generarArrayDatosCompletosDesdeTickets = (ticketsData: any[]) => {
    const arrayDatos: Array<{
      // Datos de Balanza (b)
      id_balanza: number;
      numero_ticket: number;
      fecha: string;
      cliente: string;
      cliente_id: number;
      productor: string;
      productor_id: number;
      producto: string;
      kilos: number;
      // Datos de Rollos (r)
      id_rollo: number;
      rollo_identificacion: string;
    }> = [];

    console.log('=== GENERANDO ARRAY DATOS COMPLETOS DESDE TICKETS ===');
    console.log('Tickets data recibidos:', ticketsData);

    ticketsData.forEach((ticket) => {
      console.log(`Procesando ticket ${ticket.numero_ticket} con ${ticket.rollos?.length || 0} rollos`);
      
      // Si hay rollos en el ticket
      if (ticket.rollos && ticket.rollos.length > 0) {
        console.log(`=== PROCESANDO TICKET ${ticket.numero_ticket} CON ROLLOS ===`);
        
        ticket.rollos.forEach((rollo: any) => {
          console.log(`Procesando rollo: ${rollo.rollo_identificacion} (ID: ${rollo.id_rollo})`);
          
          arrayDatos.push({
            // Datos de Balanza (b)
            id_balanza: ticket.id_balanza,
            numero_ticket: ticket.numero_ticket,
            fecha: ticket.fecha,
            cliente: ticket.cliente,
            cliente_id: 0, // No disponible en ticketsConDatos
            productor: ticket.productor,
            productor_id: 0, // No disponible en ticketsConDatos
            producto: "FIBRILLA", // Asumir producto por defecto
            kilos: ticket.kilos,
            // Datos de Rollos (r)
            id_rollo: rollo.id_rollo || 0,
            rollo_identificacion: rollo.rollo_identificacion || ""
          });
        });
      } else {
        // Si no hay rollos, simular el ticket como un rollo con rollo_id=0 y rollo_identificacion vacío
        console.log(`=== PROCESANDO TICKET ${ticket.numero_ticket} SIN ROLLOS (GRANEL) ===`);
        console.log(`Kilos del ticket: ${ticket.kilos} kg`);
        console.log(`Simulando ticket como rollo con rollo_id=0 y rollo_identificacion=""`);
        
        arrayDatos.push({
          // Datos de Balanza (b)
          id_balanza: ticket.id_balanza,
          numero_ticket: ticket.numero_ticket,
          fecha: ticket.fecha,
          cliente: ticket.cliente,
          cliente_id: 0, // No disponible en ticketsConDatos
          productor: ticket.productor,
          productor_id: 0, // No disponible en ticketsConDatos
          producto: "FIBRILLA", // Asumir producto por defecto
          kilos: ticket.kilos,
          // Datos de Rollos (r) - simulando ticket como rollo
          id_rollo: 0,
          rollo_identificacion: ""
        });
        
        console.log(`✅ Ticket ${ticket.numero_ticket} agregado al array simulando como rollo (rollo_id=0, rollo_identificacion="")`);
      }
    });

    console.log(`=== RESULTADO FINAL ===`);
    console.log(`Total de registros generados: ${arrayDatos.length}`);
    console.log(`Array de datos:`, arrayDatos);
    
    return arrayDatos;
  };

  // Log para verificar el estado antes del render
  console.log('🔍 Estado antes del render - hayTicketsValidados:', hayTicketsValidados, 'pesajes.length:', pesajes.length, 'hayResultados:', hayResultados);

  return (
    <div style={{ padding: '0' }}>
      {/* Título de la página */}
      <h1 style={{ 
        color: theme.colors.primaryDark, 
        fontSize: '22px', 
        fontWeight: 600, 
        marginBottom: '12px' 
      }}>
        Producción Manual
      </h1>

      <Card
        style={{ 
          backgroundColor: '#ffffff',
          borderRadius: theme.styles.borderRadius,
          boxShadow: theme.styles.boxShadow,
          maxWidth: '1400px'
        }}
        styles={{
          header: {
            backgroundColor: theme.colors.primaryDark,
            color: 'white',
            borderRadius: '8px 8px 0 0',
            fontSize: '14px',
            fontWeight: 600,
            padding: '8px 16px'
          }
        }}
        title="Ticket de Balanza a Desmotar"
      >
      <div style={{ padding: '12px' }}>
        <div style={{ marginBottom: '12px', textAlign: 'center' }}>
          <Text strong style={{ 
            fontSize: '13px',
            color: theme.colors.textPrimary
          }}>
            Números de Ticket Balanza
          </Text>
        </div>

        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '12px',
          marginBottom: '16px'
        }}>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Ticket 1 <span style={{ color: theme.colors.error }}>*</span>
            </label>
            <Input
              type="number"
              placeholder="Ticket 1"
              value={ticket1}
              onChange={(e) => setTicket1(e.target.value)}
              size="middle"
              style={{ 
                width: '100%',
                fontSize: '13px',
                borderColor: !ticket1 && loading ? theme.colors.error : undefined 
              }}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Ticket 2
            </label>
            <Input
              type="number"
              placeholder="Ticket 2"
              value={ticket2}
              onChange={(e) => setTicket2(e.target.value)}
              size="middle"
              style={{ width: '100%', fontSize: '13px' }}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Ticket 3
            </label>
            <Input
              type="number"
              placeholder="Ticket 3"
              value={ticket3}
              onChange={(e) => setTicket3(e.target.value)}
              size="middle"
              style={{ width: '100%', fontSize: '13px' }}
            />
          </div>
          <div>
            <label style={{ 
              display: 'block', 
              marginBottom: '4px', 
              fontWeight: 600,
              color: theme.colors.textPrimary,
              fontSize: '12px'
            }}>
              Ticket 4
            </label>
            <Input
              type="number"
              placeholder="Ticket 4"
              value={ticket4}
              onChange={(e) => setTicket4(e.target.value)}
              size="middle"
              style={{ width: '100%', fontSize: '13px' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ 
            display: 'block', 
            marginBottom: '4px', 
            fontWeight: 600,
            color: theme.colors.textPrimary,
            fontSize: '12px'
          }}>
            Producto <span style={{ color: theme.colors.error }}>*</span>
          </label>
          <Select
            style={{ width: '100%' }}
            placeholder="Seleccione un producto"
            value={productoSeleccionado}
            onChange={handleProductoChange}
            size="middle"
          >
            {productos.map(producto => (
              <Option key={producto.id} value={producto.id}>
                {producto.descripcion}
              </Option>
            ))}
          </Select>
        </div>

        <div style={{ marginBottom: '12px', textAlign: 'center' }}>
          <Text type="secondary" style={{ fontSize: '11px' }}>
            Los campos marcados con <span style={{ color: theme.colors.error }}>*</span> son obligatorios
          </Text>
        </div>

        {/* Botones de acción */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '8px',
          marginTop: '16px',
          paddingTop: '12px',
          borderTop: `1px solid ${theme.colors.borderLight}`
        }}>
          <Button
            type="primary"
            size="middle"
            onClick={handleSubmit}
            loading={loading}
            style={{
              backgroundColor: theme.colors.secondary,
              borderColor: theme.colors.secondary,
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px'
            }}
          >
            Enviar
          </Button>
          <Button
            type="primary"
            size="middle"
            onClick={handleDesmotar}
            disabled={!hayTicketsValidados}
            style={{
              fontWeight: 500,
              minWidth: '100px',
              height: '32px',
              fontSize: '13px',
              opacity: hayTicketsValidados ? 1 : 0.6
            }}
          >
            Desmotar
          </Button>
        </div>
      </div>
      </Card>

      {registros.length > 0 && (
        <Card 
          style={{ 
            marginTop: '12px',
            backgroundColor: '#ffffff',
            borderRadius: theme.styles.borderRadius,
            boxShadow: theme.styles.boxShadow
          }}
          styles={{
            header: {
              backgroundColor: theme.colors.primaryDark,
              color: 'white',
              borderRadius: '8px 8px 0 0',
              fontSize: '13px',
              fontWeight: 600,
              padding: '6px 12px'
            }
          }}
          title="Registros Seleccionados"
        >
          <Row style={{ marginBottom: '12px', textAlign: 'center' }}>
            <Col span={24}>
              <Space size="small">
                <Text strong style={{ color: theme.colors.textPrimary, fontSize: '12px' }}>Seleccionar/Deseleccionar Todos:</Text>
                <input
                  type="checkbox"
                  checked={todosRollosSeleccionados}
                  onChange={(e) => handleSeleccionarTodosRegistros(e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <Text type="secondary" style={{ fontSize: '11px' }}>
                  <strong>Nota:</strong> Los registros marcados como "Desmotado" no se pueden seleccionar y no se incluirán en el proceso de desmote.
                </Text>
              </Space>
            </Col>
          </Row>
          <Table<RegistroProduccion>
            columns={registrosColumns}
            dataSource={registros}
            rowKey={(record) => `${record.id}-${record.rollo_id || 'no-rollo'}`}
            pagination={false}
            scroll={{ x: 'max-content' }}
            size="small"
          />
        </Card>
      )}
    </div>
  );
};

export default A_Produccion; 